import { useMemo } from 'react';

import { type TeamWinnerWithLBData, type WinnerWithLBData } from '@pkg/components/Sections/Admin';
import { getPagesContent } from '@pkg/hooks';

import { type Competition, type Prize, type TeamLeaderboardItem, type TeamWinner, type Winner } from '@app/client';
import { useGetCompetitionTeamLeaderboard, useLeaderboardItems } from '@app/pages/FantasyWishlist/hooks';
import { type GameItemData } from '@app/pages/FantasyWishlist/sections/LeaderboardSection/types';

interface UseWinnerDataProps {
    gameData?: Competition;
    isTeams?: boolean;
    leaderboardPlayerItems?: GameItemData[];
    leaderboardTeamItems?: TeamLeaderboardItem[];
}
export function useWinnerData({
    gameData,
    leaderboardPlayerItems,
    leaderboardTeamItems,
    isTeams = false,
}: UseWinnerDataProps) {
    const { playerItems } = useLeaderboardItems({ interval: false });
    const { data: teamsLeaderboardRes } = useGetCompetitionTeamLeaderboard({
        disabled: !isTeams,
        interval: false,
    });
    const teamsLeaderboard = getPagesContent(teamsLeaderboardRes?.pages);

    const teamLBData = useMemo(
        () =>
            gameData?.team_winners
                ?.map((winner: TeamWinner) => {
                    const winnerLBData = leaderboardTeamItems?.find(
                        (LBItem: TeamLeaderboardItem) => LBItem?.team_id === winner?.team_id,
                    );
                    if (winnerLBData) {
                        const { team_id, ...rest } = winnerLBData;
                        return {
                            ...winner,
                            ...rest,
                        } as TeamWinnerWithLBData;
                    } else return winner;
                })
                .filter((item) => item !== undefined),
        [gameData?.team_winners, leaderboardTeamItems],
    );

    const playerLBData = useMemo(
        () =>
            gameData?.winners
                ?.map((winner: Winner) => {
                    const winnerLBData = leaderboardPlayerItems?.find(
                        (LBItem: GameItemData) => LBItem?.user_id === winner?.user_id,
                    );
                    if (winnerLBData) {
                        const { user_id, id, ...rest } = winnerLBData;
                        return {
                            ...winner,
                            ...rest,
                        } as WinnerWithLBData;
                    } else return winner;
                })
                .filter((item) => item !== undefined && item !== null),
        [gameData?.winners, leaderboardPlayerItems],
    );
    const remainingPrizeItems = useMemo(() => {
        const winnerData = isTeams ? teamLBData : playerLBData;
        const winnerItemsPrizes = winnerData?.map((w) => w?.prize);
        const prizes = isTeams ? gameData?.team_prizes : gameData?.prizes;

        return prizes?.filter((prize: Prize) => {
            const inWinnerPrizes = !!winnerItemsPrizes?.find(
                (p) => p?.reward === prize?.reward && p?.rank === prize?.rank,
            );
            if (!inWinnerPrizes) {
                return prize;
            }
        });
    }, [gameData, isTeams, playerLBData, teamLBData]);
    return {
        teamLBData,
        playerLBData,
        remainingPrizeItems,
        leaderboardData: isTeams ? teamsLeaderboard : playerItems,
    };
}
